/**
 * Shared strings for the profile-nav-package
 */
import {t} from "@lingui/core/macro";

/**
 * Returns a localized description of an avatar style, given the style name.
 * For example, for "leafers_seed_style" it returns "Leafers (Seed)".
 * When an avatar model name is used instead of a style, it returns the
 * description of the default style of that avatar, e.g. "blobby" returns
 * "Blob with superhero mask" which corresponds to the blobby_green style.
 *
 * NOTE (emilyjanzer): This is replacing the strings in util_avatar_parts.py
 * and util_avatars.py. For now, the descriptions need to be kept in sync
 * with the default images in those files. Eventually we'll just delete the
 * Python endpoints entirely.
 */
// sync-start:avatar-model-default-style 1867179433 https://github.com/Khan/webapp/blob/master/services/rewards/avatars/metadata/avatars.go
export function getAvatarStyleDescription(avatarStyle: string): string {
    switch (avatarStyle) {
        // Blobby styles
        case "blobby": // Use default style for description (green)
        case "blobby_green_style":
            return t`Blob with a superhero mask`;
        case "blobby_yellow_style":
            return t`Yellow blob`;
        case "blobby_blue_style":
            return t`Blob with an ice cream hat`;
        case "blobby_purple_style":
            return t`Blob with a snorkel mask`;

        // Area 52 styles
        case "area-52": // Use default style for description (yellow)
        case "area_52_yellow_style":
            return t`Yellow alien with a party hat`;
        case "area_52_blue_style":
            return t`Light blue alien with a party hat`;
        case "area_52_green_style":
            return t`Green alien with a party hat`;
        case "area_52_purple_style":
            return t`Purple alien with a party hat`;

        // Eggleston styles
        case "eggleston": // Use default style for description (blue)
        case "eggleston_blue_style":
            return t`Light blue egg with glasses`;
        case "eggleston_yellow_style":
            return t`Yellow egg with glasses`;
        case "eggleston_green_style":
            return t`Green egg with glasses`;
        case "eggleston_orange_style":
            return t`Orange egg with glasses`;

        // Scuttlebug styles
        case "scuttlebug": // Use default style for description (purple)
        case "scuttlebug_purple_style":
            return t`Purple bug with 6 legs`;
        case "scuttlebug_yellow_style":
            return t`Yellow bug with 6 legs`;
        case "scuttlebug_blue_style":
            return t`Light blue bug with 6 legs`;
        case "scuttlebug_green_style":
            return t`Green bug with 6 legs`;

        // Boggle styles
        case "boggle": // Use default style for description (yellow)
        case "boggle_yellow_style":
            return t`Yellow swamp monster`;
        case "boggle_blue_style":
            return t`Dark blue swamp monster`;
        case "boggle_green_style":
            return t`Green swamp monster`;
        case "boggle_purple_style":
            return t`Purple swamp monster`;

        // Sneak Peak styles
        case "sneak-peak": // Use default style for description (green)
        case "sneak_peak_green_style":
            return t`Green mountain with trees`;
        case "sneak_peak_yellow_style":
            return t`Yellow mountain with snow`;
        case "sneak_peak_blue_style":
            return t`Light blue mountain with snow`;
        case "sneak_peak_purple_style":
            return t`Purple mountain`;

        // Stelly styles
        case "stelly": // Use default style for description (blue)
        case "stelly_blue_style":
            return t`Light blue star`;
        case "stelly_yellow_style":
            return t`Yellow star`;
        case "stelly_green_style":
            return t`Green star`;
        case "stelly_orange_style":
            return t`Orange star`;

        // Cacteye styles
        case "cacteye": // Use default style for description (purple)
        case "cacteye_purple_style":
            return t`Purple cactus`;
        case "cacteye_yellow_style":
            return t`Yellow cactus`;
        case "cacteye_blue_style":
            return t`Light blue cactus`;
        case "cacteye_green_style":
            return t`Green cactus`;

        // Leaf styles
        case "leaf":
            return t`Leaf`;
        case "greenleaf":
        case "leaf_green_style":
            return t`Leaf (Green)`;
        case "blueleaf":
        case "leaf_blue_style":
            return t`Leaf (Blue)`;
        case "greyleaf":
        case "leaf_grey_style":
            return t`Leaf (Grey)`;
        case "redleaf":
        case "leaf_red_style":
            return t`Leaf (Red)`;
        case "orangeleaf":
        case "leaf_orange_style":
            return t`Leaf (Orange)`;
        case "yellowleaf":
        case "leaf_yellow_style":
            return t`Leaf (Yellow)`;

        // Leafers styles
        case "leafers":
            return t`Leafers`;
        case "leafers-seed":
        case "leafers_seed_style":
            return t`Leafers (Seed)`;
        case "leafers-seedling":
        case "leafers_seedling_style":
            return t`Leafers (Seedling)`;
        case "leafers-sapling":
        case "leafers_sapling_style":
            return t`Leafers (Sapling)`;
        case "leafers-tree":
        case "leafers_tree_style":
            return t`Leafers (Tree)`;
        case "leafers-ultimate":
        case "leafers_ultimate_style":
            return t`Leafers (Ultimate)`;

        // Piceratops styles
        case "piceratops":
            return t`πceratops`;
        case "piceratops-seed":
        case "piceratops_seed_style":
            return t`πceratops (Seed)`;
        case "piceratops-seedling":
        case "piceratops_seedling_style":
            return t`πceratops (Seedling)`;
        case "piceratops-sapling":
        case "piceratops_sapling_style":
            return t`πceratops (Sapling)`;
        case "piceratops-tree":
        case "piceratops_tree_style":
            return t`πceratops (Tree)`;
        case "piceratops-ultimate":
        case "piceratops_ultimate_style":
            return t`πceratops (Ultimate)`;

        // Duskpin styles
        case "duskpin":
            return t`Duskpin`;
        case "duskpin_seed_style":
            return t`Duskpin (Seed)`;
        case "duskpin_seedling_style":
            return t`Duskpin (Seedling)`;
        case "duskpin_sapling_style":
            return t`Duskpin (Sapling)`;
        case "duskpin_tree_style":
            return t`Duskpin (Tree)`;
        case "duskpin_ultimate_style":
            return t`Duskpin (Ultimate)`;

        // Primosaur styles
        case "primosaur":
            return t`Primosaur`;
        case "primosaur_seed_style":
            return t`Primosaur (Seed)`;
        case "primosaur_seedling_style":
            return t`Primosaur (Seedling)`;
        case "primosaur_sapling_style":
            return t`Primosaur (Sapling)`;
        case "primosaur_tree_style":
            return t`Primosaur (Tree)`;
        case "primosaur_ultimate_style":
            return t`Primosaur (Ultimate)`;

        // Starky styles
        case "starky":
            return t`Starky`;
        case "starky_seed_style":
            return t`Starky (Seed)`;
        case "starky_seedling_style":
            return t`Starky (Seedling)`;
        case "starky_sapling_style":
            return t`Starky (Sapling)`;
        case "starky_tree_style":
            return t`Starky (Tree)`;
        case "starky_ultimate_style":
            return t`Starky (Ultimate)`;

        // Aqualine styles
        case "aqualine":
            return t`Aqualine`;
        case "aqualine_seed_style":
            return t`Aqualine (Seed)`;
        case "aqualine_seedling_style":
            return t`Aqualine (Seedling)`;
        case "aqualine_sapling_style":
            return t`Aqualine (Sapling)`;
        case "aqualine_tree_style":
            return t`Aqualine (Tree)`;
        case "aqualine_ultimate_style":
            return t`Aqualine (Ultimate)`;

        // Spunky Sam styles
        case "spunky_sam":
        case "spunkysam":
            return t`Spunky Sam`;
        case "spunky_sam_blue_style":
            return t`Spunky Sam (Blue)`;
        case "spunky_sam_green_style":
            return t`Spunky Sam (Green)`;
        case "spunky_sam_orange_style":
            return t`Spunky Sam (Orange)`;
        case "spunky_sam_red_style":
            return t`Spunky Sam (Red)`;

        // Marcimus styles
        case "marcimus":
            return t`Marcimus`;
        case "marcimus_pink_style":
            return t`Marcimus (Pink)`;
        case "marcimus_orange_style":
            return t`Marcimus (Orange)`;
        case "marcimus_red_style":
            return t`Marcimus (Red)`;
        case "marcimus_purple_style":
            return t`Marcimus (Purple)`;

        // Mr. Pink styles
        case "mr_pink":
        case "mrpink":
            return t`Mr. Pink`;
        case "mr_pink_red_style":
            return t`Mr. Pink (Red)`;
        case "mr_pink_green_style":
            return t`Mr. Pink (Green)`;
        case "mr_pink_orange_style":
            return t`Mr. Pink (Orange)`;

        // Female Robot styles
        case "female_robot":
        case "amelia":
        case "female_robot_amelia_style":
            return t`Amelia`;
        case "female_robot_ada_style":
            return t`Ada`;
        case "female_robot_grace_style":
            return t`Grace`;

        // Male Robot styles
        case "male_robot":
        case "johnny":
        case "male_robot_johnny_style":
            return t`Johnny`;
        case "male_robot_donald_style":
            return t`Donald`;
        case "male_robot_hal_style":
            return t`Hal`;

        // Orange Juice Squid styles
        case "orange_juice_squid":
        case "ojsquid":
        case "orange_juice_squid_orange_style":
            return t`Orange Juice Squid`;

        // Purple Pi styles
        case "purple_pi":
        case "purplepi":
            return t`Purple Pi`;
        case "purple_pi_purple_style":
            return t`Purple Pi (Purple)`;
        case "purple_pi_pink_style":
            return t`Purple Pi (Pink)`;
        case "purple_pi_teal_style":
            return t`Purple Pi (Teal)`;

        // Mr. Pants styles
        case "mr_pants":
        case "mrpants":
            return t`Pants`;
        case "mr_pants_teal_style":
            return t`Pants (Teal)`;
        case "mr_pants_green_style":
            return t`Pants (Green)`;
        case "mr_pants_orange_style":
            return t`Pants (Orange)`;
        case "mr_pants_pink_style":
            return t`Pants (Pink)`;
        case "mr_pants_purple_style":
            return t`Pants (Purple)`;

        // Old Spice Man styles
        case "old_spice_man":
        case "ospiceman":
            return t`Old Spice Man`;
        case "old_spice_man_green_style":
            return t`Old Spice Man (Green)`;
        case "old_spice_man_blue_style":
            return t`Old Spice Man (Blue)`;

        // Winston styles
        case "winston":
        case "cs-winston":
        case "winston_default_style":
            return t`Winston`;
        case "winston_baby_style":
            return t`Baby Winston`;

        // Oh Noes styles
        case "ohnoes":
        case "cs-ohnoes":
        case "ohnoes_default_style":
            return t`Oh noes, the Error Buddy`;

        // Hopper styles
        case "hopper":
        case "cs-hopper-happy":
        case "hopper_happy_style":
            return t`Hopper`;
        case "cs-hopper-jumping":
        case "hopper_jumping_style":
            return t`Jumping Hopper`;
        case "cs-hopper-cool":
        case "hopper_cool_style":
            return t`Cool Hopper`;

        // Super Sal
        case "supersal":
            return t`Super Sal`;

        default:
            return "";
    }
}
// sync-end:avatar-model-default-style

/**
 * Returns a localized display name for an avatar, given its model name or
 * part (style) name. This returns the general name for the avatar type, not
 * the specific style label (with some exceptions). For example, for
 * "leafers_seed_style" it returns "Leafers", not "Leafers (Seed)".
 */
export function getAvatarDisplayName(avatar: string): string {
    switch (avatar) {
        // Blobby styles
        case "blobby":
        case "blobby_green_style":
        case "blobby_yellow_style":
        case "blobby_blue_style":
        case "blobby_purple_style":
            return t`Blobby`;

        // Area 52 styles
        case "area-52":
        case "area_52_yellow_style":
        case "area_52_blue_style":
        case "area_52_green_style":
        case "area_52_purple_style":
            return t`Area 52`;

        // Eggleston styles
        case "eggleston":
        case "eggleston_blue_style":
        case "eggleston_yellow_style":
        case "eggleston_green_style":
        case "eggleston_orange_style":
            return t`Eggleston`;

        // Scuttlebug styles
        case "scuttlebug":
        case "scuttlebug_purple_style":
        case "scuttlebug_yellow_style":
        case "scuttlebug_blue_style":
        case "scuttlebug_green_style":
            return t`Scuttlebug`;

        // Boggle styles
        case "boggle":
        case "boggle_yellow_style":
        case "boggle_blue_style":
        case "boggle_green_style":
        case "boggle_purple_style":
            return t`Boggle`;

        // Sneak Peak styles
        case "sneak-peak":
        case "sneak_peak_green_style":
        case "sneak_peak_yellow_style":
        case "sneak_peak_blue_style":
        case "sneak_peak_purple_style":
            return t`Sneak Peak`;

        // Stelly styles
        case "stelly":
        case "stelly_blue_style":
        case "stelly_yellow_style":
        case "stelly_green_style":
        case "stelly_orange_style":
            return t`Stelly`;

        // Cacteye styles
        case "cacteye":
        case "cacteye_purple_style":
        case "cacteye_yellow_style":
        case "cacteye_blue_style":
        case "cacteye_green_style":
            return t`Cacteye`;

        // Leaf styles
        case "leaf":
        case "greenleaf":
        case "leaf_green_style":
        case "blueleaf":
        case "leaf_blue_style":
        case "greyleaf":
        case "leaf_grey_style":
        case "redleaf":
        case "leaf_red_style":
        case "orangeleaf":
        case "leaf_orange_style":
        case "yellowleaf":
        case "leaf_yellow_style":
            return t`Leaf`;

        // Leafers styles
        case "leafers":
        case "leafers-seed":
        case "leafers_seed_style":
        case "leafers-seedling":
        case "leafers_seedling_style":
        case "leafers-sapling":
        case "leafers_sapling_style":
        case "leafers-tree":
        case "leafers_tree_style":
        case "leafers-ultimate":
        case "leafers_ultimate_style":
            return t`Leafers`;

        // Piceratops styles
        case "piceratops":
        case "piceratops-seed":
        case "piceratops_seed_style":
        case "piceratops-seedling":
        case "piceratops_seedling_style":
        case "piceratops-sapling":
        case "piceratops_sapling_style":
        case "piceratops-tree":
        case "piceratops_tree_style":
        case "piceratops-ultimate":
        case "piceratops_ultimate_style":
            return t`πceratops`;

        // Duskpin styles
        case "duskpin":
        case "duskpin_seed_style":
        case "duskpin_seedling_style":
        case "duskpin_sapling_style":
        case "duskpin_tree_style":
        case "duskpin_ultimate_style":
            return t`Duskpin`;

        // Primosaur styles
        case "primosaur":
        case "primosaur_seed_style":
        case "primosaur_seedling_style":
        case "primosaur_sapling_style":
        case "primosaur_tree_style":
        case "primosaur_ultimate_style":
            return t`Primosaur`;

        // Starky styles
        case "starky":
        case "starky_seed_style":
        case "starky_seedling_style":
        case "starky_sapling_style":
        case "starky_tree_style":
        case "starky_ultimate_style":
            return t`Starky`;

        // Aqualine styles
        case "aqualine":
        case "aqualine_seed_style":
        case "aqualine_seedling_style":
        case "aqualine_sapling_style":
        case "aqualine_tree_style":
        case "aqualine_ultimate_style":
            return t`Aqualine`;

        // Spunky Sam styles
        case "spunky_sam":
        case "spunkysam":
        case "spunky_sam_blue_style":
        case "spunky_sam_green_style":
        case "spunky_sam_orange_style":
        case "spunky_sam_red_style":
            return t`Spunky Sam`;

        // Marcimus styles
        case "marcimus":
        case "marcimus_pink_style":
        case "marcimus_orange_style":
        case "marcimus_red_style":
        case "marcimus_purple_style":
            return t`Marcimus`;

        // Mr. Pink styles
        case "mr_pink":
        case "mrpink":
        case "mr_pink_red_style":
        case "mr_pink_green_style":
        case "mr_pink_orange_style":
            return t`Mr. Pink`;

        // Female Robot styles
        case "female_robot":
        case "amelia":
        case "female_robot_amelia_style":
            return t`Amelia`;
        case "female_robot_ada_style":
            return t`Ada`;
        case "female_robot_grace_style":
            return t`Grace`;

        // Male Robot styles
        case "male_robot":
        case "johnny":
        case "male_robot_johnny_style":
            return t`Johnny`;
        case "male_robot_donald_style":
            return t`Donald`;
        case "male_robot_hal_style":
            return t`Hal`;

        // Orange Juice Squid styles
        case "orange_juice_squid":
        case "ojsquid":
        case "orange_juice_squid_orange_style":
            return t`Orange Juice Squid`;

        // Purple Pi styles
        case "purple_pi":
        case "purplepi":
        case "purple_pi_purple_style":
        case "purple_pi_pink_style":
        case "purple_pi_teal_style":
            return t`Purple Pi`;

        // Mr. Pants styles
        case "mr_pants":
        case "mrpants":
        case "mr_pants_teal_style":
        case "mr_pants_green_style":
        case "mr_pants_orange_style":
        case "mr_pants_pink_style":
        case "mr_pants_purple_style":
            return t`Pants`;

        // Old Spice Man styles
        case "old_spice_man":
        case "ospiceman":
        case "old_spice_man_green_style":
        case "old_spice_man_blue_style":
            return t`Old Spice Man`;

        // Winston styles
        case "winston":
        case "cs-winston":
        case "winston_default_style":
            return t`Winston`;
        case "winston_baby_style":
            return t`Baby Winston`;

        // Oh Noes styles
        case "ohnoes":
        case "cs-ohnoes":
        case "ohnoes_default_style":
            return t`Oh noes, the Error Buddy`;

        // Hopper styles
        case "hopper":
        case "cs-hopper-happy":
        case "hopper_happy_style":
            return t`Hopper`;
        case "cs-hopper-jumping":
        case "hopper_jumping_style":
            return t`Jumping Hopper`;
        case "cs-hopper-cool":
        case "hopper_cool_style":
            return t`Cool Hopper`;

        // Super Sal
        case "supersal":
            return t`Super Sal`;

        default:
            return "";
    }
}
